/**
 * DataTable Component
 *
 * A comprehensive, reusable data table component with:
 * - Sortable columns
 * - Row selection (single & bulk)
 * - Expandable rows
 * - Inline action buttons (no dropdown)
 * - Pagination
 * - Loading/empty states
 * - Mobile-responsive card view
 */

import React, { useMemo, useCallback } from 'react';
import { DataTableProps, SelectionState, SortState } from './types';
import { DataTableHeader } from './DataTableHeader';
import { DataTableRow } from './DataTableRow';
import { DataTableMobileCard } from './DataTableMobileCard';
import { DataTablePagination } from './DataTablePagination';
import { DataTableSkeleton } from './DataTableSkeleton';

// Default empty state icon
const DefaultEmptyIcon = () => (
  <svg
    className="h-12 w-12 text-gray-300"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
    />
  </svg>
);

// Mobile skeleton component
const MobileSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 space-y-3">
            <div className="h-5 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-100 rounded w-1/2" />
            <div className="h-4 bg-gray-100 rounded w-2/3" />
          </div>
          <div className="flex gap-2">
            <div className="h-8 w-8 bg-gray-200 rounded" />
            <div className="h-8 w-8 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export function DataTable<T extends { id?: string }>({
  data,
  columns,
  actions,

  // Loading & Empty
  loading = false,
  loadingComponent,
  emptyState,
  emptyMessage = 'No data found',
  emptyIcon,

  // Selection
  selectable = false,
  selectedIds,
  onSelectionChange,
  maxSelection = 100,

  // Sorting
  sortable = false,
  sortState,
  onSortChange,

  // Pagination
  pagination,
  onPageChange,
  onLimitChange,
  pageSizeOptions = [10, 20, 50, 100],

  // Expansion
  expandable = false,
  expandedIds,
  onExpansionChange,
  renderExpanded,
  onRowExpand,

  // Row Styling
  rowClassName,
  rowBackgroundClass,
  onRowClick,
  isRowClickable,
  striped = true,
  hoverable = true,

  // Table Styling
  className = '',
  tableClassName = '',
  actionsWidth,
  fullWidth = true,
  stickyHeader = true,
  maxHeight,

  // Mobile
  mobileBreakpoint = 768,
  forceCardView = false,
  renderMobileCard,

  // Misc
  getRowId = (row: T) => (row.id || '') as string,
  ariaLabel = 'Data table'
}: DataTableProps<T>) {
  // ============================================
  // CSS-first responsive: No JavaScript detection needed
  // Mobile cards (<640px) and table (≥640px) are both rendered,
  // CSS classes control visibility for instant switching
  // ============================================

  // ============================================
  // Selection Logic
  // ============================================
  // Memoize to prevent new Set creation on every render
  const internalSelectedIds = useMemo(
    () => selectedIds || new Set<string>(),
    [selectedIds]
  );

  const selectionState: SelectionState = useMemo(() => {
    const visibleIds = new Set(data.map(getRowId));
    const selectedVisible = [...internalSelectedIds].filter(id => visibleIds.has(id));

    return {
      selectedIds: internalSelectedIds,
      isAllSelected: data.length > 0 && selectedVisible.length === Math.min(data.length, maxSelection),
      isIndeterminate: selectedVisible.length > 0 && selectedVisible.length < Math.min(data.length, maxSelection)
    };
  }, [data, internalSelectedIds, getRowId, maxSelection]);

  const handleSelectRow = useCallback((id: string) => {
    if (!onSelectionChange) return;

    const next = new Set(internalSelectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      if (next.size >= maxSelection) return;
      next.add(id);
    }
    onSelectionChange(next);
  }, [internalSelectedIds, onSelectionChange, maxSelection]);

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;

    if (selectionState.isAllSelected) {
      // Deselect all visible
      const visibleIds = new Set(data.map(getRowId));
      const next = new Set(internalSelectedIds);
      visibleIds.forEach(id => next.delete(id));
      onSelectionChange(next);
    } else {
      // Select all visible (up to max)
      const idsToSelect = data.slice(0, maxSelection).map(getRowId);
      const next = new Set(internalSelectedIds);
      idsToSelect.forEach(id => next.add(id));
      onSelectionChange(next);
    }
  }, [data, getRowId, maxSelection, selectionState.isAllSelected, internalSelectedIds, onSelectionChange]);

  // ============================================
  // Expansion Logic
  // ============================================
  // Memoize to prevent new Set creation on every render
  const internalExpandedIds = useMemo(
    () => expandedIds || new Set<string>(),
    [expandedIds]
  );

  const handleToggleExpand = useCallback((id: string) => {
    if (!onExpansionChange) return;

    const next = new Set(internalExpandedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onExpansionChange(next);
  }, [internalExpandedIds, onExpansionChange]);

  // ============================================
  // Sort Logic
  // ============================================
  const handleSort = useCallback((sort: SortState) => {
    if (onSortChange) {
      onSortChange(sort);
    }
  }, [onSortChange]);

  // ============================================
  // Render Loading State (CSS-first: both skeletons rendered, CSS controls visibility)
  // ============================================
  if (loading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }

    return (
      <div className={className}>
        {/* Mobile skeleton - visible < 640px */}
        <div className="sm:hidden">
          <MobileSkeleton rows={5} />
        </div>
        {/* Desktop skeleton - visible ≥ 640px */}
        <div className="hidden sm:block">
          <DataTableSkeleton
            columns={columns.length}
            rows={5}
            showCheckbox={selectable}
            showExpand={expandable}
            showActions={actions && actions.length > 0}
          />
        </div>
      </div>
    );
  }

  // ============================================
  // Render Empty State
  // ============================================
  if (data.length === 0) {
    if (emptyState) {
      return <>{emptyState}</>;
    }

    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
        <div className="px-6 py-12 text-center">
          <div className="mx-auto mb-4">
            {emptyIcon || <DefaultEmptyIcon />}
          </div>
          <p className="text-gray-500">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // ============================================
  // CSS-first Responsive Layout
  // Both views are rendered, CSS controls visibility:
  // - Mobile cards: < 640px (sm:hidden)
  // - Table view: ≥ 640px (hidden sm:block)
  // Columns use hideOnTablet to show only on desktop (≥1024px)
  // ============================================
  const hasActions = actions && actions.length > 0;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* ========================================
          MOBILE CARD VIEW - visible < 640px
          ======================================== */}
      {!forceCardView && (
        <div className="sm:hidden space-y-4">
          {/* Selection header for mobile */}
          {selectable && (
            <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-3">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={selectionState.isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = selectionState.isIndeterminate;
                  }}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                Select all
              </label>
              {selectionState.selectedIds.size > 0 && (
                <span className="text-sm text-purple-600 font-medium">
                  {selectionState.selectedIds.size} selected
                </span>
              )}
            </div>
          )}

          {/* Card list */}
          <div className="space-y-3">
            {data.map((row, index) => {
              const rowId = getRowId(row);
              const isSelected = internalSelectedIds.has(rowId);
              const isExpanded = internalExpandedIds.has(rowId);

              // Use custom render if provided
              if (renderMobileCard) {
                return (
                  <div key={rowId}>
                    {renderMobileCard(row, actions)}
                  </div>
                );
              }

              return (
                <DataTableMobileCard
                  key={rowId}
                  row={row}
                  index={index}
                  columns={columns}
                  actions={actions}
                  rowId={rowId}
                  selectable={selectable}
                  isSelected={isSelected}
                  onSelect={handleSelectRow}
                  expandable={expandable}
                  isExpanded={isExpanded}
                  onToggleExpand={handleToggleExpand}
                  renderExpanded={renderExpanded}
                  onRowExpand={onRowExpand}
                  className={rowClassName?.(row, index)}
                />
              );
            })}
          </div>

          {/* Mobile Pagination */}
          {pagination && onPageChange && (
            <DataTablePagination
              pagination={pagination}
              onPageChange={onPageChange}
              onLimitChange={onLimitChange}
              pageSizeOptions={pageSizeOptions}
              loading={loading}
            />
          )}
        </div>
      )}

      {/* Force card view mode (always shows cards regardless of screen size) */}
      {forceCardView && (
        <div className="space-y-4">
          {selectable && (
            <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-3">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={selectionState.isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = selectionState.isIndeterminate;
                  }}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                Select all
              </label>
              {selectionState.selectedIds.size > 0 && (
                <span className="text-sm text-purple-600 font-medium">
                  {selectionState.selectedIds.size} selected
                </span>
              )}
            </div>
          )}
          <div className="space-y-3">
            {data.map((row, index) => {
              const rowId = getRowId(row);
              const isSelected = internalSelectedIds.has(rowId);
              const isExpanded = internalExpandedIds.has(rowId);

              if (renderMobileCard) {
                return <div key={rowId}>{renderMobileCard(row, actions)}</div>;
              }

              return (
                <DataTableMobileCard
                  key={rowId}
                  row={row}
                  index={index}
                  columns={columns}
                  actions={actions}
                  rowId={rowId}
                  selectable={selectable}
                  isSelected={isSelected}
                  onSelect={handleSelectRow}
                  expandable={expandable}
                  isExpanded={isExpanded}
                  onToggleExpand={handleToggleExpand}
                  renderExpanded={renderExpanded}
                  onRowExpand={onRowExpand}
                  className={rowClassName?.(row, index)}
                />
              );
            })}
          </div>
          {pagination && onPageChange && (
            <DataTablePagination
              pagination={pagination}
              onPageChange={onPageChange}
              onLimitChange={onLimitChange}
              pageSizeOptions={pageSizeOptions}
              loading={loading}
            />
          )}
        </div>
      )}

      {/* ========================================
          TABLE VIEW - visible ≥ 640px
          Columns with hideOnTablet show only ≥ 1024px
          ======================================== */}
      {!forceCardView && (
        <div className="hidden sm:block">
          <div
            className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto ${
              maxHeight ? 'overflow-y-auto' : 'overflow-y-hidden'
            }`}
            style={{ maxHeight }}
          >
            <table
              className={`${fullWidth ? 'w-full' : ''} divide-y divide-gray-200 ${tableClassName}`}
              aria-label={ariaLabel}
            >
              <DataTableHeader
                columns={columns}
                sortState={sortState}
                onSortChange={sortable ? handleSort : undefined}
                selectable={selectable}
                selectionState={selectionState}
                onSelectAll={handleSelectAll}
                expandable={expandable}
                hasActions={hasActions}
                actionsWidth={actionsWidth}
              />

              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((row, index) => {
                  const rowId = getRowId(row);
                  const isSelected = internalSelectedIds.has(rowId);
                  const isExpanded = internalExpandedIds.has(rowId);

                  return (
                    <DataTableRow
                      key={rowId}
                      row={row}
                      index={index}
                      columns={columns}
                      actions={actions}
                      rowId={rowId}
                      selectable={selectable}
                      isSelected={isSelected}
                      onSelect={handleSelectRow}
                      expandable={expandable}
                      isExpanded={isExpanded}
                      onToggleExpand={handleToggleExpand}
                      renderExpanded={renderExpanded}
                      onRowExpand={onRowExpand}
                      className={rowClassName?.(row, index)}
                      backgroundClass={rowBackgroundClass?.(row, index)}
                      onClick={onRowClick && (isRowClickable?.(row) ?? true) ? onRowClick : undefined}
                      striped={striped}
                      hoverable={hoverable}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Table Pagination */}
          {pagination && onPageChange && (
            <DataTablePagination
              pagination={pagination}
              onPageChange={onPageChange}
              onLimitChange={onLimitChange}
              pageSizeOptions={pageSizeOptions}
              loading={loading}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default DataTable;
